import { isSaveOnlyTerminalStep } from '../../shared/lib/sop-terminal-delivery.domain.js';
import { customListValidationIssues } from '../../shared/lib/custom-list-step.domain.js';
import {
  multipleChoiceValidationIssues,
} from '../../shared/lib/multiple-choice-step.domain.js';
import {
  evaluateSopFlowStructure,
  listSopStepAuthoringIssues,
  dedupeCyclePaths,
  formatSopFlowCycleSummary,
  sopFlowIncompleteCauseBlocksSave,
  sopFlowIncompletePathKey,
  type SopFlowIncompletePath,
} from '../../shared/lib/sop-flow-validation.domain.js';
import { getSopStepOutgoingEdges } from '../../shared/lib/sop-step-edges.domain.js';
import { STOCK_BINARY_POSITIVE_TEMPLATE } from '../../shared/lib/sop-narrative-stock.domain.js';
import {
  buildSopStepDisplayNumberMap,
  resolveSopStepDisplayNumber,
  sortSopSteps,
  type SopStepRow,
} from '@/lib/sops-admin';
import {
  projectSopDraftStepsForFlowHealth,
  type SopDraftFlowProjection,
} from '@/lib/sop-authoring-draft-client';

const QUESTION_ARCHETYPES = new Set(['BINARY_CHOICE', 'YES_NO_QUESTION', 'TRUE_FALSE']);

export type SopStepLinkKind = 'next' | 'yes' | 'no' | 'option';

export type SopIncomingLink = {
  sourceStep: SopStepRow;
  kind: SopStepLinkKind;
  label: string;
};

export type SopStepOutgoingLink = {
  kind: SopStepLinkKind;
  label: string;
  field: 'next_step' | 'yes_next_step' | 'no_next_step' | `option:${string}`;
  targetId: string | number | null | undefined;
  optionId?: string;
};

export type SopFlowHealthIssueSegment =
  | { kind: 'text'; text: string }
  | { kind: 'step'; stepId: number; displayNumber: number; title: string; loopReentry?: boolean };

export type SopFlowHealthIssue = {
  kind:
    | 'missing-terminal'
    | 'missing-incoming'
    | 'missing-outgoing'
    | 'orphan-target'
    | 'empty'
    | 'contact-warning'
    | 'custom-list-warning'
    | 'narrative-stock-warning'
    | 'step-configuration'
    | 'cycle'
    | 'incomplete-flow';
  label: string;
  message: string;
  stepId?: number;
  /** When true, link saves are blocked until resolved; false = warning-only in Flow Health. */
  blocksSave: boolean;
  /** Stable list key for React and dedupe; path-based issues use cause + step path signature. */
  issueKey: string;
  /** Optional rich label (step badge + suffix). */
  labelSegments?: SopFlowHealthIssueSegment[];
  /** Primary explanation line. */
  body?: SopFlowHealthIssueSegment[];
  /** Action hint on a second line. */
  hint?: SopFlowHealthIssueSegment[];
};

export type SopFlowHealth = {
  issueCount: number;
  issues: SopFlowHealthIssue[];
  missingIncoming: SopStepRow[];
  missingOutgoing: SopStepRow[];
  orphanTargets: Array<{ step: SopStepRow; link: SopStepOutgoingLink }>;
};

export const EMPTY_SOP_FLOW_HEALTH: SopFlowHealth = {
  issueCount: 0,
  issues: [],
  missingIncoming: [],
  missingOutgoing: [],
  orphanTargets: [],
};

export function sopFlowHealthStepSegment(
  step: Pick<SopStepRow, 'id' | 'step_order' | 'archetype' | 'variant' | 'config' | 'title'>,
  displayNumbers: Map<number, number>,
): Extract<SopFlowHealthIssueSegment, { kind: 'step' }> {
  const stepId = Number(step.id);
  return {
    kind: 'step',
    stepId,
    displayNumber: resolveSopStepDisplayNumber(step, displayNumbers),
    title: getStepDisplayTitle(step),
  };
}

function stepSegmentById(
  steps: SopStepRow[],
  stepId: string | number,
  displayNumbers: Map<number, number>,
): Extract<SopFlowHealthIssueSegment, { kind: 'step' }> | null {
  const step = steps.find((row) => Number(row.id) === Number(stepId));
  if (!step) return null;
  return sopFlowHealthStepSegment(step, displayNumbers);
}

export function sopFlowHealthSegmentsToPlainText(segments: SopFlowHealthIssueSegment[]): string {
  return segments
    .map((segment) =>
      segment.kind === 'text' ? segment.text : `Step ${segment.displayNumber} "${segment.title}"`,
    )
    .join('');
}

function labelWithStep(
  step: SopStepRow,
  displayNumbers: Map<number, number>,
  suffix: string,
): Pick<SopFlowHealthIssue, 'label' | 'labelSegments'> {
  const segment = sopFlowHealthStepSegment(step, displayNumbers);
  return {
    label: `Step ${segment.displayNumber}${suffix}`,
    labelSegments: [segment, { kind: 'text', text: suffix }],
  };
}

function mapSharedStructureIssues(steps: SopStepRow[]): SopFlowHealthIssue[] {
  const structure = evaluateSopFlowStructure(steps);
  if (structure.valid !== false) return [];

  const displayNumbers = buildSopStepDisplayNumberMap(sortSopSteps(steps));

  switch (structure.issue) {
    case 'no_steps':
      return [{
        kind: 'empty',
        label: 'No steps',
        message: 'This SOP has no workflow steps yet.',
        blocksSave: false,
        issueKey: 'empty',
      }];
    case 'no_end_step':
    case 'no_submit_terminal':
    case 'no_reachable_submit_terminal':
      return [{
        kind: 'missing-terminal',
        label: 'Missing submit terminal',
        message: structure.details ?? structure.reason,
        blocksSave: true,
        issueKey: `missing-terminal-${structure.issue}`,
      }];
    case 'incomplete_flow': {
      const paths = dedupeCyclePaths(structure.incompletePaths ?? [], steps);
      if (!paths.length) {
        return [{
          kind: 'incomplete-flow',
          label: 'Incomplete flow',
          message: structure.details ?? structure.reason,
          blocksSave: true,
          issueKey: 'incomplete-flow-unknown',
        }];
      }
      return dedupeHealthIssuesByStepAndCause(
        paths.flatMap((entry) => mapIncompletePathIssue(entry, steps, displayNumbers)),
      );
    }
    default:
      return [];
  }
}

function dedupeHealthIssuesByStepAndCause(issues: SopFlowHealthIssue[]): SopFlowHealthIssue[] {
  const seen = new Set<string>();
  return issues.filter((issue) => {
    if (issue.kind !== 'incomplete-flow') return true;
    const cause = issue.issueKey.split(':')[0] ?? issue.kind;
    const key = `${issue.stepId ?? 'none'}:${cause}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildLoopChainSegments(
  steps: SopStepRow[],
  loopStepIds: string[],
  reentryStepId: string | number,
  displayNumbers: Map<number, number>,
): SopFlowHealthIssueSegment[] {
  const segments: SopFlowHealthIssueSegment[] = [{ kind: 'text', text: '. Loop: ' }];
  const reentryId = Number(reentryStepId);
  let addedStep = false;
  for (const stepId of loopStepIds) {
    const segment = stepSegmentById(steps, stepId, displayNumbers);
    if (!segment) continue;
    if (addedStep) segments.push({ kind: 'text', text: ' ↓ ' });
    segments.push({
      ...segment,
      loopReentry: Number.isFinite(reentryId) && reentryId > 0 && Number(stepId) === reentryId,
    });
    addedStep = true;
  }
  segments.push({ kind: 'text', text: '.' });
  return segments;
}

function mapIncompletePathIssue(
  entry: SopFlowIncompletePath,
  steps: SopStepRow[],
  displayNumbers: Map<number, number>,
): SopFlowHealthIssue[] {
  const pathIds = entry.path.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  const focusStepId = pathIds[pathIds.length - 1] ?? pathIds[0];
  if (entry.cause === 'cycle') {
    const displayNumberForId = (stepId: string) => {
      const step = steps.find((row) => Number(row.id) === Number(stepId));
      if (!step) return undefined;
      return resolveSopStepDisplayNumber(step, displayNumbers);
    };
    const cycle = formatSopFlowCycleSummary(steps, entry, displayNumberForId);
    const fromSeg = stepSegmentById(steps, cycle.fromStepId, displayNumbers);
    const toSeg = stepSegmentById(steps, cycle.toStepId, displayNumbers);
    const branchSuffix =
      cycle.branch && cycle.branch !== 'Next' ? ` (${cycle.branch})` : '';
    const fixHint = ' That path must lead to an End SOP terminal.';
    const loopSegments = buildLoopChainSegments(steps, cycle.loopStepIds, cycle.toStepId, displayNumbers);
    const body: SopFlowHealthIssueSegment[] =
      fromSeg && toSeg
        ? [
            fromSeg,
            { kind: 'text', text: `${branchSuffix} links to ` },
            toSeg,
            ...loopSegments,
          ]
        : [{ kind: 'text', text: cycle.summary }];
    const hint: SopFlowHealthIssueSegment[] = fromSeg
      ? [
          { kind: 'text', text: 'Change the link on ' },
          fromSeg,
          ...(branchSuffix ? [{ kind: 'text' as const, text: branchSuffix }] : []),
          { kind: 'text', text: fixHint },
        ]
      : [{ kind: 'text', text: `${cycle.fixAt}${fixHint}` }];
    const message = sopFlowHealthSegmentsToPlainText([...body, ...hint]);
    return [{
      kind: 'cycle',
      label: 'Flow loop detected',
      message,
      body,
      hint,
      stepId: cycle.focusStepId ?? focusStepId,
      blocksSave: true,
      issueKey: sopFlowIncompletePathKey(entry),
    }];
  }
  const focusStep = focusStepId ? steps.find((row) => Number(row.id) === focusStepId) : undefined;
  const labelParts = focusStep
    ? labelWithStep(focusStep, displayNumbers, ' has an incomplete path')
    : { label: 'Incomplete flow path' as const };
  return [{
    kind: 'incomplete-flow',
    ...labelParts,
    message: focusStep
      ? `${getStepDisplayTitle(focusStep)}: ${structureCauseMessage(entry.cause)}`
      : structureCauseMessage(entry.cause),
    stepId: focusStepId,
    blocksSave: sopFlowIncompleteCauseBlocksSave(entry.cause),
    issueKey: sopFlowIncompletePathKey(entry),
  }];
}

function structureCauseMessage(cause: string): string {
  switch (cause) {
    case 'missing-next-step':
      return 'This step has no next step linked.';
    case 'missing-yes-path':
    case 'missing-no-path':
    case 'missing-option-path':
      return 'This branch needs a destination.';
    case 'invalid-destination':
    case 'step-not-found':
      return 'This branch points to a step that no longer exists.';
    default:
      return 'This execution path does not reach an End SOP terminal.';
  }
}

/** Steps safe to render for the selected SOP (excludes stale rows during load or id mismatch). */
export function resolveDocumentSteps(
  selectedSopId: number | null,
  steps: SopStepRow[],
  stepsLoading: boolean,
): SopStepRow[] {
  if (!selectedSopId || stepsLoading) return [];
  if (steps.length && steps.some((step) => step.sop_id != null && Number(step.sop_id) !== Number(selectedSopId))) {
    return [];
  }
  return steps;
}

export function buildSopStructuralHealth(documentSteps: SopStepRow[], stepsLoading: boolean): SopFlowHealth {
  if (stepsLoading) return EMPTY_SOP_FLOW_HEALTH;
  return buildSopFlowHealth(documentSteps);
}

export function normalizeStepLinkValue(value: unknown): number | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric !== 0 ? numeric : null;
}

export function getStepDisplayTitle(step: Pick<SopStepRow, 'archetype' | 'variant' | 'config' | 'title'>): string {
  if (String(step.archetype || '').toUpperCase() === 'TERMINAL') {
    return isSaveOnlyTerminalStep(step) ? 'Save Report Only' : 'End SOP';
  }
  return String(step.title || '').trim() || 'Untitled step';
}

export function getSopStepSummary(step: SopStepRow): string {
  const prompt = String(step.prompt_text || '').trim();
  if (prompt) return prompt.replace(/\s+/g, ' ');
  if (step.archetype === 'CONTACT_ACTION') return 'Contact action step';
  if (step.archetype === 'RESOURCE_VIEW') return step.url_reference_value || step.url || 'Resource step';
  if (step.archetype === 'BINARY_CHOICE') return 'Branching decision step';
  if (step.archetype === 'MULTI_CHOICE') return 'Multi-path branch step';
  if (step.archetype === 'TERMINAL') return 'Workflow endpoint';
  return 'No operator instructions yet';
}

export function edgeKindFromHandle(handle: string | null | undefined): SopStepLinkKind {
  if (handle === 'yes') return 'yes';
  if (handle === 'no') return 'no';
  if (handle?.startsWith('option:')) return 'option';
  return 'next';
}

export function optionIdFromHandle(handle: string | null | undefined): string | undefined {
  if (!handle?.startsWith('option:')) return undefined;
  return handle.slice('option:'.length) || undefined;
}

function rawMultipleChoiceOptions(config: unknown): Array<Record<string, unknown>> {
  let source = config;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return [];
    }
  }
  if (source == null || typeof source !== 'object') return [];
  const options = (source as Record<string, unknown>).options;
  if (!Array.isArray(options)) return [];
  return options.filter(
    (option): option is Record<string, unknown> => option != null && typeof option === 'object',
  );
}

export function getSopStepOutgoingLinks(step: SopStepRow): SopStepOutgoingLink[] {
  const rawOptions = rawMultipleChoiceOptions(step.config);
  let optionIndex = 0;
  return getSopStepOutgoingEdges(step).map((edge) => {
    if (edge.kind === 'yes') {
      return {
        kind: 'yes' as const,
        label: edge.label,
        field: 'yes_next_step' as const,
        targetId: normalizeStepLinkValue(step.yes_next_step),
      };
    }
    if (edge.kind === 'no') {
      return {
        kind: 'no' as const,
        label: edge.label,
        field: 'no_next_step' as const,
        targetId: normalizeStepLinkValue(step.no_next_step),
      };
    }
    if (edge.kind === 'option') {
      const optionId = edge.optionId || '';
      const rawOption = rawOptions[optionIndex];
      optionIndex += 1;
      return {
        kind: 'option' as const,
        optionId,
        label: edge.label,
        field: `option:${optionId}` as const,
        targetId: normalizeStepLinkValue(
          rawOption?.next_step_id ?? rawOption?.nextStepId ?? rawOption?.next_step,
        ),
      };
    }
    return {
      kind: 'next' as const,
      label: edge.label,
      field: 'next_step' as const,
      targetId: normalizeStepLinkValue(step.next_step),
    };
  });
}

export function buildIncomingLinksByTarget(steps: SopStepRow[]): Map<number, SopIncomingLink[]> {
  const sorted = sortSopSteps(steps);
  const byId = new Map(sorted.map((step) => [Number(step.id), step]));
  const firstStepId = Number(sorted[0]?.id || 0);
  const incomingByTarget = new Map<number, SopIncomingLink[]>();
  const dedupe = new Set<string>();

  for (const sourceStep of sorted) {
    for (const link of getSopStepOutgoingLinks(sourceStep)) {
      const targetId = normalizeStepLinkValue(link.targetId);
      if (!targetId || !byId.has(targetId)) continue;
      const from = Number(sourceStep.id);
      if (from === targetId) continue;
      if (targetId === firstStepId) continue;
      const key = `${from}->${targetId}:${link.kind}`;
      if (dedupe.has(key)) continue;
      dedupe.add(key);
      const list = incomingByTarget.get(targetId) ?? [];
      list.push({ sourceStep, kind: link.kind, label: link.label });
      incomingByTarget.set(targetId, list);
    }
  }

  return incomingByTarget;
}

function remapFlowHealthIssue(
  issue: SopFlowHealthIssue,
  projection: SopDraftFlowProjection,
): SopFlowHealthIssue {
  const remapId = (id: number) => projection.validationIdToLocalId.get(Number(id)) ?? id;
  const remapSegments = (segments?: SopFlowHealthIssueSegment[]) =>
    segments?.map((segment) =>
      segment.kind === 'step' ? { ...segment, stepId: remapId(segment.stepId) } : segment,
    );
  const issueKey = issue.issueKey.replace(/\d+/g, (value) => {
    const mapped = projection.validationIdToLocalId.get(Number(value));
    return mapped == null ? value : String(mapped);
  });
  return {
    ...issue,
    stepId: issue.stepId == null ? issue.stepId : remapId(issue.stepId),
    issueKey,
    labelSegments: remapSegments(issue.labelSegments),
    body: remapSegments(issue.body),
    hint: remapSegments(issue.hint),
  };
}

function remapFlowHealth(
  health: SopFlowHealth,
  projection: SopDraftFlowProjection,
  originalSteps: SopStepRow[],
): SopFlowHealth {
  const originalById = new Map(originalSteps.map((step) => [Number(step.id), step]));
  const remapStep = (step: SopStepRow) =>
    originalById.get(projection.validationIdToLocalId.get(Number(step.id)) ?? Number(step.id)) ?? step;
  return {
    ...health,
    issues: health.issues.map((issue) => remapFlowHealthIssue(issue, projection)),
    missingIncoming: health.missingIncoming.map(remapStep),
    missingOutgoing: health.missingOutgoing.map(remapStep),
    orphanTargets: health.orphanTargets.map(({ step, link }) => ({
      step: remapStep(step),
      link: {
        ...link,
        targetId: link.targetId == null
          ? link.targetId
          : projection.validationIdToLocalId.get(Number(link.targetId)) ?? link.targetId,
      },
    })),
  };
}

export function buildSopFlowHealth(steps: SopStepRow[]): SopFlowHealth {
  const projection = projectSopDraftStepsForFlowHealth(steps);
  if (projection) {
    return remapFlowHealth(buildSopFlowHealthForValidatedSteps(projection.steps), projection, steps);
  }
  return buildSopFlowHealthForValidatedSteps(steps);
}

function buildSopFlowHealthForValidatedSteps(steps: SopStepRow[]): SopFlowHealth {
  const sorted = sortSopSteps(steps);
  const displayNumbers = buildSopStepDisplayNumberMap(sorted);

  const sharedIssues = mapSharedStructureIssues(sorted);
  if (sharedIssues.some((issue) => issue.kind === 'empty')) {
    return { issueCount: sharedIssues.length, issues: sharedIssues, missingIncoming: [], missingOutgoing: [], orphanTargets: [] };
  }

  const issues: SopFlowHealthIssue[] = [...sharedIssues];
  const sharedStructural = sharedIssues.some(
    (issue) => issue.kind === 'missing-terminal' || issue.kind === 'cycle' || issue.kind === 'incomplete-flow',
  );

  const byId = new Map(sorted.map((step) => [Number(step.id), step]));
  const firstStepId = Number(sorted[0]?.id || 0);
  const incoming = new Map<number, number>();
  const orphanTargets: Array<{ step: SopStepRow; link: SopStepOutgoingLink }> = [];

  for (const [authoringIndex, authoringIssue] of listSopStepAuthoringIssues(sorted).entries()) {
    const step = sorted.find((row) => Number(row.id) === Number(authoringIssue.stepId));
    if (!step) continue;
    issues.push({
      kind: 'step-configuration',
      ...labelWithStep(step, displayNumbers, ' needs configuration'),
      message: authoringIssue.message,
      stepId: Number(step.id),
      blocksSave: true,
      issueKey: `step-configuration-${step.id}-${authoringIssue.kind}-${authoringIndex}`,
    });
  }

  for (const step of sorted) {
    for (const link of getSopStepOutgoingLinks(step)) {
      const target = normalizeStepLinkValue(link.targetId);
      if (!target) continue;
      if (!byId.has(target)) {
        orphanTargets.push({ step, link });
        continue;
      }
      incoming.set(target, (incoming.get(target) || 0) + 1);
    }
  }

  orphanTargets.forEach(({ step, link }) => {
    issues.push({
      kind: 'orphan-target',
      ...labelWithStep(step, displayNumbers, ' points to a missing step'),
      message: `${link.label} targets step ID ${link.targetId}, which was not loaded in this SOP.`,
      stepId: step.id,
      blocksSave: true,
      issueKey: `orphan-target-${step.id}-${link.field}`,
    });
  });

  // Narrative quality warnings always run (including on structurally healthy SOPs).
  for (const step of sorted) {
    if (String(step.archetype || '').toUpperCase() === 'MULTI_CHOICE') {
      const configIssues = multipleChoiceValidationIssues(step.config, {
        includeInReport: step.include_in_report !== false && step.include_in_report !== 0,
      });
      for (const issue of configIssues) {
        if (issue.severity !== 'warn') continue;
        issues.push({
          kind: 'custom-list-warning',
          ...labelWithStep(step, displayNumbers, ' has choices without report text'),
          message: `${getStepDisplayTitle(step)}: ${issue.message}`,
          stepId: step.id,
          blocksSave: false,
          issueKey: `multi-choice-warning-${step.id}-${issue.optionId ?? issue.code}`,
        });
      }
      continue;
    }
    if (String(step.archetype || '').toUpperCase() === 'SPECIAL_ACTION'
      && String(step.variant || '').toLowerCase() === 'custom_list') {
      const warnings = customListValidationIssues(step.config, {
        includeInReport: step.include_in_report !== false,
        legacyReportActionText: step.report_action_text,
      }).filter((issue) => issue.severity === 'warn');
      if (warnings.length) {
        issues.push({
          kind: 'custom-list-warning',
          ...labelWithStep(step, displayNumbers, ' has list options without report text'),
          message: `${getStepDisplayTitle(step)}: ${warnings.map((issue) => issue.message).join(' ')}`,
          stepId: step.id,
          blocksSave: false,
          issueKey: `custom-list-warning-${step.id}`,
        });
      }
    }

    const mode = String(step.archetype || '').toUpperCase();
    if (QUESTION_ARCHETYPES.has(mode) || mode === 'BINARY_CHOICE' || mode === 'TRUE_FALSE' || mode === 'YES_NO_QUESTION') {
      const positive = String(step.positive_response_format || '').trim();
      if (step.include_in_report !== false && positive === STOCK_BINARY_POSITIVE_TEMPLATE) {
        issues.push({
          kind: 'narrative-stock-warning',
          ...labelWithStep(step, displayNumbers, ' uses stock Yes report text'),
          message: `${getStepDisplayTitle(step)} still uses the stock Yes template “operator observed {prompt}”. Edit it or acknowledge Keep as written during publish review.`,
          stepId: step.id,
          blocksSave: false,
          issueKey: `narrative-stock-${step.id}`,
        });
      }
    }
  }

  if (!sharedStructural) {
    const hasSubmitTerminal = sorted.some(
      (step) => String(step.archetype || '').toUpperCase() === 'TERMINAL' && !isSaveOnlyTerminalStep(step),
    );
    if (!hasSubmitTerminal) {
      issues.push({
        kind: 'missing-terminal',
        label: 'Missing submit terminal',
        message: 'Add an End SOP terminal so at least one path can submit the report.',
        blocksSave: true,
        issueKey: 'missing-terminal-local',
      });
    }

    const missingIncoming = sorted.filter((step) => {
      if (Number(step.id) === firstStepId) return false;
      if (String(step.archetype || '').toUpperCase() === 'TERMINAL') return false;
      return !incoming.has(Number(step.id));
    });
    const missingOutgoing = sorted.filter((step) => {
      if (String(step.archetype || '').toUpperCase() === 'TERMINAL') return false;
      return getSopStepOutgoingLinks(step).some((link) => !normalizeStepLinkValue(link.targetId));
    });

    missingIncoming.forEach((step) => {
      issues.push({
        kind: 'missing-incoming',
        ...labelWithStep(step, displayNumbers, ' has no incoming link'),
        message: `${getStepDisplayTitle(step)} is not reached from another step.`,
        stepId: step.id,
        blocksSave: false,
        issueKey: `missing-incoming-${step.id}`,
      });
    });
    missingOutgoing.forEach((step) => {
      issues.push({
        kind: 'missing-outgoing',
        ...labelWithStep(step, displayNumbers, ' has an unlinked path'),
        message: `${getStepDisplayTitle(step)} needs every outgoing branch linked to a target step.`,
        stepId: step.id,
        blocksSave: false,
        issueKey: `missing-outgoing-${step.id}`,
      });
    });

    return {
      issueCount: issues.length,
      issues,
      missingIncoming,
      missingOutgoing,
      orphanTargets,
    };
  }

  return {
    issueCount: issues.length,
    issues,
    missingIncoming: [],
    missingOutgoing: [],
    orphanTargets,
  };
}

export type SopFlowHealthBadgeVariant = 'success' | 'destructive' | 'warning';

export type SopFlowHealthBadge = {
  label: string;
  variant: SopFlowHealthBadgeVariant;
};

const LIBRARY_FLOW_KIND_LABELS: Partial<Record<SopFlowHealthIssue['kind'], string>> = {
  empty: 'No steps',
  'missing-terminal': 'Missing terminal',
  'missing-incoming': 'Unreachable step',
  'missing-outgoing': 'Unlinked path',
  'orphan-target': 'Orphan link',
  'step-configuration': 'Step configuration',
  cycle: 'Flow cycle',
  'incomplete-flow': 'Incomplete path',
};

const LIBRARY_FLOW_KIND_ORDER: SopFlowHealthIssue['kind'][] = [
  'empty',
  'missing-terminal',
  'cycle',
  'incomplete-flow',
  'orphan-target',
  'step-configuration',
  'missing-outgoing',
  'missing-incoming',
];

/** Compact flow-health badges for SOP library list rows (max two). */
export function buildSopLibraryFlowHealthBadges(health: SopFlowHealth): SopFlowHealthBadge[] {
  if (health.issueCount === 0) {
    return [{ label: 'Flow healthy', variant: 'success' }];
  }
  const presentKinds = LIBRARY_FLOW_KIND_ORDER.filter((kind) =>
    health.issues.some((issue) => issue.kind === kind),
  );
  const labels = presentKinds.map((kind) => LIBRARY_FLOW_KIND_LABELS[kind] ?? kind);
  if (!labels.length) {
    return [{
      label: `${health.issueCount} flow issue${health.issueCount === 1 ? '' : 's'}`,
      variant: 'destructive',
    }];
  }
  if (labels.length === 1) {
    return [{ label: labels[0], variant: 'destructive' }];
  }
  if (labels.length === 2) {
    return labels.map((label) => ({ label, variant: 'destructive' as const }));
  }
  return [
    { label: labels[0], variant: 'destructive' },
    { label: `${health.issueCount} flow issues`, variant: 'destructive' },
  ];
}

export function isSopStepLinkIssue(issue: Pick<SopFlowHealthIssue, 'kind' | 'label'>): boolean {
  if (issue.kind === 'cycle' || issue.kind === 'incomplete-flow') return true;
  if (issue.kind === 'missing-incoming' || issue.kind === 'orphan-target') return true;
  if (issue.kind === 'missing-outgoing') return issue.label.includes('unlinked path');
  return false;
}

/** Per-step flow issues for timeline card badges. */
export function buildStepHealthIssues(steps: SopStepRow[]): Map<number, SopFlowHealthIssue[]> {
  const health = buildSopFlowHealth(steps);
  const byStep = new Map<number, SopFlowHealthIssue[]>();
  for (const issue of health.issues) {
    if (issue.kind === 'empty' || issue.kind === 'missing-terminal' || issue.stepId == null) continue;
    const id = Number(issue.stepId);
    const list = byStep.get(id) ?? [];
    list.push(issue);
    byStep.set(id, list);
  }
  return byStep;
}

export function formatStepLinkSummary(step: SopStepRow, steps: SopStepRow[]): string {
  const byId = new Map(steps.map((row) => [Number(row.id), row]));
  const resolveTitle = (targetId: unknown) => {
    const id = normalizeStepLinkValue(targetId);
    if (!id) return '—';
    const target = byId.get(id);
    return target ? getStepDisplayTitle(target) : `Step #${id}`;
  };
  const parts = getSopStepOutgoingLinks(step).map((link) => {
    const target = normalizeStepLinkValue(link.targetId);
    return `${link.label} → ${target ? resolveTitle(target) : '—'}`;
  });
  return parts.length ? parts.join(' · ') : '';
}

export function hasSubmitTerminal(steps: SopStepRow[]): boolean {
  return steps.some((step) => String(step.archetype || '').toUpperCase() === 'TERMINAL' && !isSaveOnlyTerminalStep(step));
}

export function submitTerminalStepBody(): Record<string, unknown> {
  return {
    archetype: 'TERMINAL',
    title: 'End SOP',
    prompt_text: null,
    include_in_report: false,
  };
}
